import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatchTask, useTasks, useTeam } from '../lib/api/tasks';
import { useCampaigns } from '../lib/api/campaigns';
import { Task } from '../lib/types';
import { Button } from '../components/ui/Button';
import { HowItWorksLink } from '../components/ui/HowItWorksLink';
import { Icon } from '../components/ui/Icon';
import TaskRow from '../components/tasks/TaskRow';
import CreateTaskModal from '../components/modals/CreateTaskModal';
import { useTaskActions } from '../components/tasks/useTaskActions';
import { BUCKET_LABEL, BUCKET_ORDER, groupTasks } from '../lib/taskBuckets';
import { filterTasks } from '../lib/taskSearch';
import TaskSearchBox from '../components/tasks/TaskSearchBox';

const Tasks = () => {
  const navigate = useNavigate();
  const [showCompleted, setShowCompleted] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data: tasks = [], isLoading } = useTasks();
  const { data: campaigns = [] } = useCampaigns();
  const { data: team = [] } = useTeam();
  const patchTask = usePatchTask();
  const taskActions = useTaskActions();
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));

  const unassignedCount = useMemo(
    () =>
      tasks.filter(
        (t) => (t.assignees?.length ?? 0) === 0 && t.status !== 'Done',
      ).length,
    [tasks],
  );

  const visibleTasks = useMemo(
    () =>
      filterTasks(
        unassignedOnly ? tasks.filter((t) => (t.assignees?.length ?? 0) === 0) : tasks,
        query,
        (id) => (id ? campaignById.get(id)?.name : undefined),
        team,
      ),
    // campaignById is rebuilt every render from `campaigns`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, unassignedOnly, query, campaigns, team],
  );

  const buckets = useMemo(
    () => groupTasks(visibleTasks, showCompleted),
    [visibleTasks, showCompleted],
  );
  const overdueCount = buckets.overdue.length;

  const toggle = (id: string, current: string) => {
    patchTask.mutate({
      id,
      patch: { status: current === 'Done' ? 'In progress' : 'Done' },
    });
  };

  const renderRow = (t: Task) => {
    const c = t.campaign ? campaignById.get(t.campaign) : undefined;
    return (
      <TaskRow
        key={t.id}
        task={t}
        team={team}
        campaignName={c?.name}
        campaignType={c?.type}
        onCampaignClick={(cid) => navigate(`/campaigns/${cid}`)}
        onToggle={() => toggle(t.id, t.status)}
        onAssign={(ids) => patchTask.mutate({ id: t.id, patch: { assignees: ids } })}
        onEdit={taskActions.openEdit}
        onDelete={taskActions.openDelete}
      />
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Execution board</div>
          <h1 className="page-title">Tasks</h1>
          <p className="page-sub">
            {tasks.length} task{tasks.length === 1 ? '' : 's'}
            {unassignedCount > 0 ? ` · ${unassignedCount} unassigned` : ''}
            {overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HowItWorksLink section="tasks" />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> New task
          </Button>
        </div>
      </div>

      <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
        <TaskSearchBox value={query} onChange={setQuery} />
        <button
          type="button"
          className={`filter-pill ${unassignedOnly ? 'active' : ''}`}
          onClick={() => setUnassignedOnly((v) => !v)}
          style={
            !unassignedOnly && unassignedCount > 0
              ? { background: 'var(--warn-bg)', color: 'var(--warn)', borderColor: 'var(--warn)' }
              : undefined
          }
          title={
            unassignedCount > 0
              ? `${unassignedCount} task${unassignedCount === 1 ? '' : 's'} need someone assigned`
              : 'No unassigned tasks'
          }
          disabled={unassignedCount === 0 && !unassignedOnly}
        >
          <Icon name="alert" size={11} />
          Unassigned{unassignedCount > 0 ? ` (${unassignedCount})` : ''}
        </button>
        <button
          type="button"
          className={`filter-pill ${showCompleted ? 'active' : ''}`}
          onClick={() => setShowCompleted((v) => !v)}
        >
          Show completed
        </button>
      </div>

      {isLoading && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Loading tasks…
        </div>
      )}

      {!isLoading && tasks.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>No tasks yet.</p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> Create your first task
          </Button>
        </div>
      )}

      {!isLoading && tasks.length > 0 && visibleTasks.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
          {query.trim() ? `No tasks match “${query.trim()}”.` : 'No unassigned tasks. Nicely done.'}
        </div>
      )}

      {!isLoading && visibleTasks.length > 0 &&
        BUCKET_ORDER.map((key) => {
          const ts = buckets[key];
          if (ts.length === 0) return null;
          return (
            <div key={key} className="section">
              <div className="section-head">
                <div>
                  <div className="section-title">{BUCKET_LABEL[key]}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                    {ts.length} task{ts.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <div className="card">{ts.map(renderRow)}</div>
            </div>
          );
        })}

      <CreateTaskModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {taskActions.modals}
    </div>
  );
};

export default Tasks;
